import { z } from 'zod';
import {
  Id,
  PermissionMask,
  ShareTargetType,
  ShareVersionMode,
  Timestamp,
  Uri,
  atLeastOneField,
  cursorPage,
} from './common.js';
import { Board } from './boards.js';
import { Asset } from './assets.js';

export const ShareLink = z.object({
  id: Id,
  workspace_id: Id,
  target_type: ShareTargetType,
  target_id: Id,
  target_name: z.string().optional(),
  token: z.string().optional(),
  url: Uri,
  has_password: z.boolean(),
  expires_at: Timestamp.nullable(),
  allow_download: z.boolean(),
  allow_comment: z.boolean(),
  show_metadata: z.boolean(),
  guest_mask: PermissionMask.optional(),
  view_count: z.int(),
  last_accessed_at: Timestamp.nullable().optional(),
  created_by: Id,
  created_at: Timestamp,
  revoked_at: Timestamp.nullable(),
  /** Release 2 C4: show assets whose usage rights expired (hidden by default). */
  include_expired: z.boolean().optional(),
  // --- release3 (contract) --- DEL-1 policy
  require_identity: z.boolean().optional(),
  version_mode: ShareVersionMode.optional(),
  pinned_version_id: Id.nullable().optional(),
  approved_only: z.boolean().optional(),
  complete_only: z.boolean().optional(),
});
export type ShareLink = z.infer<typeof ShareLink>;

export const ShareLinkPage = cursorPage(ShareLink);
export type ShareLinkPage = z.infer<typeof ShareLinkPage>;

export const ShareLinkCreate = z.object({
  target_type: ShareTargetType,
  target_id: Id,
  password: z.string().min(4).max(256).nullable().optional(),
  expires_at: Timestamp.nullable().optional(),
  allow_download: z.boolean().default(false),
  allow_comment: z.boolean().default(false),
  show_metadata: z.boolean().default(true),
  include_expired: z.boolean().optional(),
  // --- release3 (contract) --- DEL-1 policy (defaults false / `current`); `pinned_version_id` required iff `pinned`
  require_identity: z.boolean().optional(),
  version_mode: ShareVersionMode.optional(),
  pinned_version_id: Id.nullable().optional(),
  approved_only: z.boolean().optional(),
  complete_only: z.boolean().optional(),
}).refine((o) => ((o.version_mode ?? 'current') === 'pinned') === (o.pinned_version_id != null), {
  message: 'pinned_version_id is required exactly when version_mode is pinned',
  path: ['pinned_version_id'],
});
export type ShareLinkCreate = z.infer<typeof ShareLinkCreate>;

export const ShareLinkUpdate = atLeastOneField(
  z.object({
    password: z.string().min(4).max(256).nullable().optional(),
    expires_at: Timestamp.nullable().optional(),
    allow_download: z.boolean().optional(),
    allow_comment: z.boolean().optional(),
    show_metadata: z.boolean().optional(),
    include_expired: z.boolean().optional(),
    // --- release3 (contract) --- DEL-1 policy
    require_identity: z.boolean().optional(),
    version_mode: ShareVersionMode.optional(),
    pinned_version_id: Id.nullable().optional(),
    approved_only: z.boolean().optional(),
    complete_only: z.boolean().optional(),
  }),
);
export type ShareLinkUpdate = z.infer<typeof ShareLinkUpdate>;

export const ShareLinkAuthRequest = z.object({ password: z.string() });
export type ShareLinkAuthRequest = z.infer<typeof ShareLinkAuthRequest>;

export const PublicShareResolve = z.object({
  target_type: ShareTargetType,
  target_id: Id,
  workspace_name: z.string(),
  workspace_logo_url: Uri.nullable().optional(),
  guest_mask: PermissionMask,
  allow_download: z.boolean(),
  allow_comment: z.boolean(),
  show_metadata: z.boolean(),
  expires_at: Timestamp.nullable().optional(),
  board: Board.optional(),
  asset: Asset.optional(),
  // --- release3/B --- DEL-1: identity gate state (board / asset omitted while `identified` is false)
  require_identity: z.boolean().optional(),
  identified: z.boolean().optional(),
  // --- release3/C --- EXP-1: workspace export presets for the public download menu (empty unless `allow_download`)
  download_presets: z.array(z.object({ id: Id, name: z.string(), transform: z.record(z.string(), z.unknown()) }).loose()).optional(),
});
export type PublicShareResolve = z.infer<typeof PublicShareResolve>;

/** Inline body of `POST /public/share/{token}/download` (not a named component). */
export const PublicShareDownloadRequest = z.object({
  asset_ids: z.array(Id).optional(),
  /** Release 3 EXP-1: a workspace export preset (presets only through share links). */
  preset_id: Id.optional(),
});
export type PublicShareDownloadRequest = z.infer<typeof PublicShareDownloadRequest>;
